import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { getItem, removeSessionItem, removeItem } from '../../storeManager'
import { Link, useNavigate } from 'react-router-dom'
import { trackCompletedPayment, trackCompletedUpgrade } from '../../analytics'
import ErrorBubble from '../../components/ErrorBubble'
import { upgradeToPremium } from '../../store/userSlice'
import Payment from '../../components/Payment/Payment'
import DefaultLayout from '../../components/DefaultLayout'
import Header from '../../components/Header/Header'
import Footer from '../../components/Footer/Footer'
import { FormattedMessage } from 'react-intl'
import messages from './messages'
import Button from '../../components/Button/Button'

function FinalizePaymentPage() {
  const [showPaymentComponent, setShowPaymentComponent] = useState(false)
  const [tryAgainLink, setTryAgainLink] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const paymentStateRef = useRef(JSON.parse(getItem('paymentState')))
  const [paymentError, setPaymentError] = useState(
    new URLSearchParams(window.location.search).get('success') === '0',
  )

  useEffect(() => {
    const paymentState = paymentStateRef.current
    if (!paymentState) {
      navigate('/', { replace: true })
      return null
    }

    const track = (options = {}) => {
      var transaction_id = paymentState.responseData?.transaction_id
      if (!transaction_id) {
        transaction_id = `timestamp-${Date.now()}`
      }

      // non-3ds
      var coupon = paymentState.responseData?.product_details?.coupon_code
      if (!coupon) {
        // 3ds
        coupon = paymentState.paymentData.product?.options?.coupon_code
        if (!coupon) {
          // fallback
          coupon = paymentState.paymentProps.savedCoupon
        }
      }

      let email =
        JSON.parse(localStorage.getItem('state'))?.user?.profile?.email || null

      trackCompletedPayment(
        {
          transaction_id: transaction_id,
          currency: paymentState.paymentData.product.options.currency,
          value: paymentState.paymentData.amount,
          coupon: coupon,
          account_type: 'Premium',
          subscription_duration:
            paymentState.paymentData.product.options.duration,
          email: email,
        },
        options,
      )
    }

    const paymentInitialError =
      new URLSearchParams(window.location.search).get('success') === '0'

    removeItem('paymentState')

    if (paymentInitialError) return setPaymentError(true)

    const paymentData = paymentState.paymentData

    switch (paymentState.scenario) {
      case 'partner':
      case 'signup':
        track()
        dispatch(upgradeToPremium())
        navigate('/welcome', { state: { paymentData }, replace: true })
        break
      case 'upgrade':
        track({ actionName: 'upgrade' })
        dispatch(upgradeToPremium())
        trackCompletedUpgrade()
        removeSessionItem('upgrade')
        navigate('/upgrade/welcome', { state: { paymentData }, replace: true })
        break
      case 'renew':
        track({ purchaseLifecycle: 'renew' })
        dispatch(upgradeToPremium())
        navigate('/renew/welcome', { replace: true })
        break
      case 'update-card':
        navigate('/account/subscription', { replace: true })
        break
      case '100-percent-discount':
        dispatch(upgradeToPremium())
        navigate('/free/welcome', { replace: true })
        break
      default:
        setPaymentError(true)
    }
  }, [dispatch, navigate])

  useEffect(() => {
    const paymentState = paymentStateRef.current
    if (paymentError && paymentState.scenario) {
      switch (paymentState.scenario) {
        case 'partner':
        case 'signup':
        case 'upgrade':
        case 'renew':
          setShowPaymentComponent(true)
          break
        case 'update-card':
          setTryAgainLink('/account/subscription')
          break
      }
    }

    return
  }, [paymentError])

  return (
    <DefaultLayout>
      <Header inverted={false} />
      <div className="mx-auto mt-96 px-16 text-center md:mt-192 lg:max-w-1/2">
        {paymentError && (
          <div>
            <ErrorBubble>
              <p>
                <FormattedMessage
                  {...messages['Redeem.pay.didntGoAsPlanned']}
                />
              </p>

              <p className="mb-40 mt-24">
                <FormattedMessage
                  {...messages['Redeem.pay.contactCustomerSupport']}
                  values={{
                    a: function Link(...chunks) {
                      return <a href="https://lifesum.com/contact">{chunks}</a>
                    },
                  }}
                />
              </p>
            </ErrorBubble>
            {showPaymentComponent && (
              <div className="mx-auto max-w-512">
                <h2 className="mb-40 type-28 md:type-32">
                  <FormattedMessage
                    {...messages['UserAccount_DeleteDialog_TryAgainButtonText']}
                  />
                </h2>
                <Payment
                  {...paymentStateRef.current.paymentProps}
                  againstBeige={true}
                />
              </div>
            )}
            {tryAgainLink && (
              <div>
                <Button big use={Link} to={tryAgainLink}>
                  <FormattedMessage
                    {...messages['UserAccount_DeleteDialog_TryAgainButtonText']}
                  />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {!paymentError && <p>Finalizing payment…</p>}
      <Footer />
    </DefaultLayout>
  )
}

export default FinalizePaymentPage
